import { Injectable, signal } from '@angular/core';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  title?: string;
  timeOut?: number;
  closeButton?: boolean;
  progressBar?: boolean;
  tapToDismiss?: boolean;
  disableTimeOut?: boolean;
}

interface Alert {
  id: number;
  message: string;
  type: ToastType;
  timeoutId?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private readonly _alerts = signal<Alert[]>([]);
  public readonly alerts = this._alerts.asReadonly();
  
  private alertIdCounter = 0;
  private readonly maxAlerts = 3;
  private readonly defaultDuration = 3000;

  success(message: string, options?: ToastOptions): void {
    this.show(message, 'success', options);
  }

  error(message: string, options?: ToastOptions): void {
    this.show(message, 'error', options);
  }

  warning(message: string, options?: ToastOptions): void {
    this.show(message, 'warning', options);
  }

  info(message: string, options?: ToastOptions): void {
    this.show(message, 'info', options);
  }

  clear(): void {
    this._alerts().forEach(alert => {
      if (alert.timeoutId) {
        clearTimeout(alert.timeoutId);
      }
    });
    this._alerts.set([]);
  }

  clearById(toastId: number): void {
    const alert = this._alerts().find(a => a.id === toastId);
    if (alert?.timeoutId) {
      clearTimeout(alert.timeoutId);
    }
    this._alerts.update(alerts => alerts.filter(a => a.id !== toastId));
  }

  private show(message: string, type: ToastType, options?: ToastOptions): void {
    const newAlert: Alert = {
      id: ++this.alertIdCounter,
      message,
      type
    };

    if (!options?.disableTimeOut) {
      const duration = options?.timeOut || this.defaultDuration;
      const timeoutId = setTimeout(() => {
        this.clearById(newAlert.id);
      }, duration);
      newAlert.timeoutId = timeoutId;
    }

    this._alerts.update(alerts => {
      const updated = [...alerts, newAlert];
      if (updated.length > this.maxAlerts) {
        const removed = updated.slice(0, updated.length - this.maxAlerts);
        removed.forEach(alert => {
          if (alert.timeoutId) {
            clearTimeout(alert.timeoutId);
          }
        });
      }
      return updated.slice(-this.maxAlerts);
    });
  }
}
